import { NavigateFunction } from "react-router-dom";
import { DASHBOARD_ENTITIES, ROUTES } from "../../../common/constants";
import {
  checkIfInputIsAnArrayWithValues,
  getFileNamesFromSelectedFileIds,
  getIndexOfSystemMessageFromEnd,
  getSanitizedChatMessages,
  getToast,
  getUpdatedDocsData,
  getUpdatedDocsDataForLocal,
} from "../../../common/utils";
import {
  extractMetaDataOfDocumentForClauseCheck,
  getClauseCheckDataForDashboard,
  getClauseCheckDocumentsDataForDashboard,
  initiateClauseDataExtraction,
  uploadFileOrFoldersForClauseCheckAdvanced,
  uploadFilesForClauseCheck,
} from "../../../services/contracts.service";
import {
  convertDocToPdf,
  fileUploadFromDrive,
  uploadFromLocalForTTC,
  uploadFromLocalToDrive,
  uploadMultipleFilesToDrive,
} from "../../../services/FileUpload.service";
import { BulkClauseTableData } from "../../ManageContract/model";
import { clauseCheckDocStatus, defaultSuggestions } from "../constants";
import {
  addDocsToConversation,
  getContractsReview,
  getConversationData,
  getDocMetaData,
  getDueContractsDataForGraph,
  getAboutToExpireContractsDataForGraphV2,
  getVmoBotResponse,
} from "../dashboard.service";
import {
  ClauseCheckDocumentRequestModel,
  ClauseCheckModel,
  ContractDocument,
  ContractReviewModel,
  MetaData,
  RequestBodyForClauseCheckAdvanced,
  Suggestion,
  TTCDocumentRequestModalForLocalUpload,
  TTCDocumentRequestModel,
} from "../model";
import {
  IUploadMultiDocFromKCCThunk,
  IUploadMutliDocFromBotThunk,
} from "../types";
import { getFileNameWithoutExtension } from "../utils";
import { Auth } from "../../../auth/auth";
import { navigatingRouteFor } from "../../../components/AntdTable/AntdTable.constants";
import { updateDetailsOfConversation } from "../../../store/thunks/lazyLoad.thunk";
import { getADKConversationData } from "../../../services/adk.service";
import { PromptsForFeature } from "../../Admin/constants";

/**
 * Reads the selected contract type id for a feature from the common reducer.
 * Returns undefined when the standalone Contract Review contract-type feature
 * flag is off, so review falls back to vertical-only scoping. This flag is
 * intentionally separate from REACT_APP_IS_CONTRACT_DRAFTING_ENABLED, which
 * only controls the Admin Clause Management contract-type dropdown.
 */
const getSelectedContractTypeId = (
  getState: any,
  feature: string = PromptsForFeature.CONTRACT_REDLINING
): string | undefined => {
  if (process.env.REACT_APP_IS_CONTRACT_REVIEW_TYPE_ENABLED !== "true") {
    return undefined;
  }
  const contractTypes = getState()?.rootReducer?.commonReducer?.contractTypes;
  return contractTypes?.[feature]?.value || undefined;
};

export const CONTRACT_REVIEW_ACTIONS = {
  ADD_DOCUMENT: "ADD_DOCUMENT",
  REMOVE_DOCUMENT: "REMOVE_DOCUMENT",
  UPDATE_DETAILS_OF_UPLOADING_DOCUMENT: "UPDATE_DETAILS_OF_UPLOADING_DOCUMENT",
  UPDATE_FILE_UPLOAD_STATUS: "UPDATE_FILE_UPLOAD_STATUS",
  SET_CONTRACT_REVIEW_DATA: "SET_CONTRACT_REVIEW_DATA",
  UPDATE_PENDING_DOC_COUNT: "UPDATE_PENDING_DOC_COUNT",
};

export const CLAUSE_CHECK_ACTIONS = {
  ADD_DOCUMENT_FOR_CLAUSE_CHECK: "ADD_DOCUMENT_FOR_CLAUSE_CHECK",
  REMOVE_DOCUMENT_FROM_CLAUSE_CHECK: "REMOVE_DOCUMENT_FROM_CLAUSE_CHECK",
  UPDATE_DETAILS_OF_UPLOADING_DOCUMENT_FOR_CLAUSE_CHECK_AFTER_SUCCESSFUL_UPLOAD:
    "UPDATE_DETAILS_OF_UPLOADING_DOCUMENT_FOR_CLAUSE_CHECK_AFTER_SUCCESSFUL_UPLOAD",
  UPDATE_DATA_FOR_CLAUSE_CHECK_DOCUMENT:
    "UPDATE_DATA_FOR_CLAUSE_CHECK_DOCUMENT",
  SET_CLAUSE_CHECK_DATA: "SET_CLAUSE_CHECK_DATA",
  SET_FAILED_DOC_DATA: "SET_FAILED_DOC_DATA",
};

export const VMO_BOT_ACTIONS = {
  UPDATE_VMO_BOT_DATA: "UPDATE_VMO_BOT_DATA",
  ADD_MESSAGE_TO_CHAT: "ADD_MESSAGE_TO_CHAT",
  UPDATE_MESSAGE_DATA: "UPDATE_MESSAGE_DATA",
  REMOVE_SUGGESTION: "REMOVE_SUGGESTION",
  UPLOAD_VMO_FILES: "UPLOAD_VMO_FILES",
  UPDATE_UPLOAD_STATUS: "UPDATE_UPLOAD_STATUS",
  UPDATE_PROCESSING_STAGE: "UPDATE_PROCESSING_STAGE",
  UPDATE_PROCESSED_FILES: "UPDATE_PROCESSED_FILES",
  SET_SUGGESTIONS: "SET_SUGGESTIONS",
  SET_MESSAGE_LOADER: "SET_MESSAGE_LOADER",
  RESET_VMO_BOT_CHAT: "RESET_VMO_BOT_CHAT",
  SET_SELECTED_MODEL: "SET_SELECTED_MODEL",
  UPDATE_LAST_SYSTEM_MESSAGE: "UPDATE_LAST_SYSTEM_MESSAGE",
  UPDATE_CONVERSATION_DETAILS: "UPDATE_CONVERSATION_DETAILS",
};

export const CONTRACT_EXPIRY_DASHBOARD_ACTIONS = {
  SET_CONTRACT_EXPIRY_DASHBOARD_DATA: "SET_CONTRACT_EXPIRY_DASHBOARD_DATA",
  SET_CONTRACT_EXPIRY_DASHBOARD_GRAPH_DATA:
    "SET_CONTRACT_EXPIRY_DASHBOARD_GRAPH_DATA",
};

export const ADK_TTC_BOT_ACTIONS = {
  SET_ADK_TTC_BOT_DATA: "SET_ADK_TTC_BOT_DATA",
  RESET_ADK_CHAT_BOT: "RESET_ADK_CHAT_BOT",
};

export const setAdkTTCBotActionsThunk = (payload: any) => {
  return async (dispatch: any) => {
    try {
      dispatch({
        type: ADK_TTC_BOT_ACTIONS.SET_ADK_TTC_BOT_DATA,
        payload: payload,
      });
    } catch (error) {
      console.error("Error in setting ADK TTC Bot data:", error);
    }
  };
};

// ─── A0.2: per-vertical session scoping ─────────────────────────────────────
// localStorage key per user, so two team-mates on the same machine don't
// collide and one user's vertical→session map survives reloads.
const _verticalSessionsKey = (userId: string) => `ttc_vertical_sessions_${userId}`;

/** Read the saved map (vertical_id → session_id) for this user. */
export const loadVerticalSessionsMap = (userId: string): Record<string, string> => {
  if (!userId || typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(_verticalSessionsKey(userId));
    return raw ? JSON.parse(raw) : {};
  } catch (e) {
    return {};
  }
};

/** Persist + dispatch the map atomically. */
export const persistVerticalSessionsThunk = (
  userId: string,
  map: Record<string, string>,
) => {
  return async (dispatch: any) => {
    if (userId && typeof window !== "undefined") {
      try {
        window.localStorage.setItem(_verticalSessionsKey(userId), JSON.stringify(map));
      } catch (e) {
        console.warn("Failed to persist vertical sessions:", e);
      }
    }
    dispatch({
      type: ADK_TTC_BOT_ACTIONS.SET_ADK_TTC_BOT_DATA,
      payload: { verticalSessions: map },
    });
  };
};

// ─── Per-vertical status filter (active/expired/all) ────────────────────────
// Same per-vertical, per-user storage pattern as verticalSessions so each
// vertical can carry its own filter selection independently.
const _verticalStatusFiltersKey = (userId: string) =>
  `ttc_vertical_status_filters_${userId}`;

export type TTCStatusFilter = "active" | "expired" | "all";

export const loadVerticalStatusFiltersMap = (
  userId: string,
): Record<string, TTCStatusFilter> => {
  if (!userId || typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(_verticalStatusFiltersKey(userId));
    return raw ? JSON.parse(raw) : {};
  } catch (e) {
    return {};
  }
};

export const persistVerticalStatusFiltersThunk = (
  userId: string,
  map: Record<string, TTCStatusFilter>,
) => {
  return async (dispatch: any) => {
    if (userId && typeof window !== "undefined") {
      try {
        window.localStorage.setItem(
          _verticalStatusFiltersKey(userId),
          JSON.stringify(map),
        );
      } catch (e) {
        console.warn("Failed to persist vertical status filters:", e);
      }
    }
    dispatch({
      type: ADK_TTC_BOT_ACTIONS.SET_ADK_TTC_BOT_DATA,
      payload: { verticalStatusFilters: map },
    });
  };
};

const addClauseCheckDocument = (doc: any) => {
  return async (dispatch: any) => {
    try {
      dispatch({
        type: CLAUSE_CHECK_ACTIONS.ADD_DOCUMENT_FOR_CLAUSE_CHECK,
        payload: doc,
      });
    } catch (error) {}
  };
};

const updateDataForClauseCheckDocumentsAfterUpload = (
  file_id: string,
  doc: any
) => {
  return async (dispatch: any) => {
    try {
      dispatch({
        type: CLAUSE_CHECK_ACTIONS.UPDATE_DETAILS_OF_UPLOADING_DOCUMENT_FOR_CLAUSE_CHECK_AFTER_SUCCESSFUL_UPLOAD,
        payload: {
          file_id: file_id,
          doc: doc,
        },
      });
    } catch (error) {}
  };
};

const updateDataForClauseCheckDocument = (file_id: any, data: any) => {
  return async (dispatch: any) => {
    try {
      dispatch({
        type: CLAUSE_CHECK_ACTIONS.UPDATE_DATA_FOR_CLAUSE_CHECK_DOCUMENT,
        payload: {
          file_id: file_id,
          data: data,
        },
      });
    } catch (err) {}
  };
};

export const uploadFileForClauseCheckAdvancedThunk = ({
  docs,
  vertical_id,
}: IUploadMultiDocFromKCCThunk) => {
  return async (dispatch: any) => {
    const auth = Auth.getInstance();
    const token = auth.getGoogleAccessToken();
    const fileObjectsForRequestBody = new RequestBodyForClauseCheckAdvanced({
      ...docs,
      vertical_id,
      token,
    });

    const formData = new FormData();
    Object.keys(fileObjectsForRequestBody).forEach((key) => {
      const value = (fileObjectsForRequestBody as any)[key];
     
      if (value) {
        if (key === 'files') {
          value.forEach((file: File) => {
            formData.append('files', file);
          });
        } else {
          formData.append(key, value);
        }
      }
    });
    console.log("fileObjectsForRequestBody-->", fileObjectsForRequestBody);
    await uploadFileOrFoldersForClauseCheckAdvanced(formData);
  };
};

export const uploadFileForClauseCheckThunk = ({
  docs,
  vertical_id,
}: IUploadMultiDocFromKCCThunk) => {
  return async (dispatch: any) => {
    const { files, driveFiles } = docs;
    try {
      dispatch({
        type: CLAUSE_CHECK_ACTIONS.SET_CLAUSE_CHECK_DATA,
        payload: {
          uploadProcessCompleted: false,
        },
      });
      // Create Contract row structure to accommodate response
      const fileObjectsForResponseBody = files
        .map((doc: any) => {
          return {
            ...new ContractDocument(doc),
            loading: true,
            status: clauseCheckDocStatus.META_DATA_TO_BE_EXTRACTED,
          };
        })
        .concat(
          driveFiles.map((doc: any) => {
            return {
              ...new ContractDocument(doc),
              loading: true,
              status: clauseCheckDocStatus.META_DATA_TO_BE_EXTRACTED,
            };
          })
        );

      fileObjectsForResponseBody?.forEach(async (doc: any) => {
        await dispatch(addClauseCheckDocument(doc));
      });

      const fileObjectsForRequestBodyForDriveFiles = driveFiles.map(
        (doc: any) => new ClauseCheckDocumentRequestModel(doc)
      );

      const auth = Auth.getInstance();
      let formData: FormData = new FormData();
      files?.forEach((file: File) => formData.append("files", file));
      formData.append(
        "file_data",
        JSON.stringify(fileObjectsForRequestBodyForDriveFiles)
      );
      formData.append("vertical_id", vertical_id);
      formData.append("token", JSON.stringify(auth.getGoogleAccessToken()));

      const res = await uploadFilesForClauseCheck(formData);

      // On successfull upload
      if (checkIfInputIsAnArrayWithValues(res?.data)) {
        // 2nd pass to update the documents with the response from upload API
        res?.data?.forEach(async (doc: any) => {
          await dispatch(
            updateDataForClauseCheckDocumentsAfterUpload(
              doc?.drive_details?.file_id,
              doc
            )
          );
        });

        // 3rd pass for calling metadata API in loop for all uploaded documents
        const promises =
          res?.data?.map(async (doc: any, index: number) => {
            try {
              const response = await extractMetaDataOfDocumentForClauseCheck({
                doc_id: doc?.id,
              });

              // Updating metadata for a document, disabling the loader for document, updating the metadata status
              await dispatch(
                updateDataForClauseCheckDocument(doc?.drive_details?.file_id, {
                  metadata: new MetaData(response?.metadata),
                  loading: false,
                  status: clauseCheckDocStatus.META_DATA_EXTRACTED,
                })
              );

              // initiate clause check extraction for all documents
              initiateClauseDataExtraction({
                doc_id: doc?.id,
              });

              await dispatch(
                updateDataForClauseCheckDocument(doc?.drive_details?.file_id, {
                  status: clauseCheckDocStatus.CLAUSE_CHECK_IN_PROGRESS,
                })
              );
              return { success: true };
            } catch (error: any) {
              return {
                success: false,
                errorMessage:
                  error?.response?.data?.error || "Something went wrong",
              };
            }
          }) || [];

        const results = await Promise.all(promises);
        const allSuccessful = results.every((result) => {
          return result.success;
        });
        if (!allSuccessful) {
          getToast(
            "Error",
            results?.find((result) => !result.success)?.errorMessage
          );
        } else {
          dispatch({
            type: CLAUSE_CHECK_ACTIONS.SET_CLAUSE_CHECK_DATA,
            payload: {
              uploadProcessCompleted: true,
            },
          });
        }
      }
    } catch (error: any) {
      getToast("Error", error?.response?.data?.error || "Something went wrong");
      dispatch({
        type: CLAUSE_CHECK_ACTIONS.REMOVE_DOCUMENT_FROM_CLAUSE_CHECK,
      });
    }
  };
};

export const uploadFileForContractReviewThunk = ({
  file,
  fileDetails,
  isLocal,
  vertical_id,
  vertical,
  navigateObject,
  tableEntity,
}: {
  file: File;
  fileDetails: {
    file_name: string;
    source_file_name?: string;
    content_type?: string;
    id?: string;
  };
  isLocal: boolean;
  vertical_id?: string;
  vertical?: any;
  navigateObject: NavigateFunction;
  tableEntity?: string;
}) => {
  return async (dispatch: any, getState: any) => {
    const file_name = getFileNameWithoutExtension(fileDetails.file_name);
    const contract_type_id = getSelectedContractTypeId(getState);
    const fileData = new ContractDocument({
      file_name: file_name,
      status: "review_pending",
      id: String(new Date().getTime()),
    });
    try {
      dispatch({
        type: CONTRACT_REVIEW_ACTIONS.ADD_DOCUMENT,
        payload: fileData,
      });

      dispatch({
        type: CONTRACT_REVIEW_ACTIONS.UPDATE_FILE_UPLOAD_STATUS,
        payload: {
          isUploading: true,
          error: {},
          uploaded: false,
        },
      });

      const res = isLocal
        ? await uploadFromLocalToDrive({
            file: file,
            vertical_id: vertical_id as string,
            contract_type_id: contract_type_id,
          })
        : await fileUploadFromDrive({
            file_name: fileDetails.file_name,
            file_id: fileDetails?.id || "",
            vertical_id: vertical_id as string,
            contract_type_id: contract_type_id,
          });

      const docId = res?.data?.id;
      let newFileData = {
        ...fileData,
        id: docId,
      };

      await convertDocToPdf({
        file_name: fileDetails.file_name,
      });

      const data = await getDocMetaData({ doc_id: docId });
      newFileData = {
        ...newFileData,
        metadata: new MetaData(data?.metadata),
      };

      dispatch({
        type: CONTRACT_REVIEW_ACTIONS.UPDATE_DETAILS_OF_UPLOADING_DOCUMENT,
        payload: newFileData,
      });

      dispatch({
        type: CONTRACT_REVIEW_ACTIONS.UPDATE_PENDING_DOC_COUNT,
        payload: {},
      });

      dispatch({
        type: CONTRACT_REVIEW_ACTIONS.UPDATE_FILE_UPLOAD_STATUS,
        payload: {
          isUploading: false,
          error: {},
          uploaded: true,
        },
      });

      getToast(
        "Success",
        `File uploaded successfully to ${
          vertical?.label || vertical?.display_name
        } vertical`
      );

      const url = `${ROUTES.doc}?url=${docId}&backurl=${navigatingRouteFor(
        tableEntity
      )}`;
      navigateObject(url);
    } catch (error) {
      dispatch({
        type: CONTRACT_REVIEW_ACTIONS.UPDATE_FILE_UPLOAD_STATUS,
        payload: {
          isUploading: false,
          error: error,
          uploaded: false,
        },
      });
      dispatch({
        type: CONTRACT_REVIEW_ACTIONS.REMOVE_DOCUMENT,
        payload: {},
      });
      getToast("Error", "File could not be uploaded.");
    }
  };
};

export const uploadMutliDocFromBotThunk = ({
  docs,
  alreadyUploadedDocs = [],
  isLocal,
}: IUploadMutliDocFromBotThunk) => {
  return async (dispatch: any, getState: any) => {
    try {
      const systemMessageId = new Date().getTime();

      const checkKey = isLocal ? "name" : "id";

      docs = docs?.filter(
        (doc: any) => !alreadyUploadedDocs?.includes(doc?.[checkKey])
      );

      const docsToBeUploaded: any = docs?.map(
        (doc: any) => new TTCDocumentRequestModel(doc)
      );

      const docsToBeUploadedForLocal: any = docs?.map(
        (doc: any) => new TTCDocumentRequestModalForLocalUpload(doc)
      );

      if (docs?.length === 0) {
        return getToast(
          "Error",
          "These files are already uploaded. No new files to added."
        );
      }

      dispatch({
        type: VMO_BOT_ACTIONS.UPLOAD_VMO_FILES,
        payload: isLocal ? docsToBeUploadedForLocal : docs,
      });

      dispatch({
        type: VMO_BOT_ACTIONS.UPDATE_UPLOAD_STATUS,
        payload: true,
      });

      dispatch({
        type: VMO_BOT_ACTIONS.UPDATE_PROCESSING_STAGE,
        payload: "ShowUploadedFiles",
      });

      await dispatch({
        type: VMO_BOT_ACTIONS.ADD_MESSAGE_TO_CHAT,
        payload: {
          sender: "system",
          text: isLocal ? docsToBeUploadedForLocal : docs,
          id: systemMessageId,
        },
      });

      const vmoBotData = getState()?.rootReducer?.vscDashboardReducer?.vmoBot;

      const { conversationId, selectedFiles, selectedModel } = vmoBotData;

      let formData: FormData = new FormData();
      if (isLocal) {
        docs?.map((file: File) => formData.append("files", file));
        if (conversationId) {
          formData.append("chat_session_id", conversationId);
        }
      }

      const res = isLocal
        ? await uploadFromLocalForTTC(formData)
        : await uploadMultipleFilesToDrive({
            file_objects: docsToBeUploaded,
            ...(conversationId ? { chat_session_id: conversationId } : {}),
          });

      const data = isLocal ? res?.data?.uploaded_documents : res?.data;

      dispatch({
        type: VMO_BOT_ACTIONS.UPDATE_LAST_SYSTEM_MESSAGE,
        payload: {
          chatMessageIdx: getIndexOfSystemMessageFromEnd(vmoBotData?.chat),
          data: data,
          ...(isLocal ? { id: systemMessageId } : {}),
        },
      });

      const { updatedProcessedDocs, filteredUploadedDocs } = isLocal
        ? getUpdatedDocsDataForLocal(data)
        : getUpdatedDocsData(docs, data);

      if (data) {
        dispatch({
          type: VMO_BOT_ACTIONS.UPLOAD_VMO_FILES,
          payload: filteredUploadedDocs,
        });

        dispatch({
          type: VMO_BOT_ACTIONS.UPDATE_UPLOAD_STATUS,
          payload: false,
        });

        dispatch({
          type: VMO_BOT_ACTIONS.UPDATE_PROCESSED_FILES,
          payload: updatedProcessedDocs,
        });
      }

      const idsOfNewlyUploadedFiles = data?.map((doc: any) => doc?.id) || [];

      dispatch(
        updateVmoBotThunkDetails({
          selectedFiles: selectedFiles.concat(idsOfNewlyUploadedFiles),
        })
      );

      if (data?.length !== 0) {
        const addDocsToConversationRes = await addDocsToConversation({
          document_ids: idsOfNewlyUploadedFiles,
          ...(conversationId && { chat_session_id: conversationId }),
          model: selectedModel,
        });

        const { chat_session_id, chat_session_title } =
          addDocsToConversationRes;

        if (chat_session_id) {
          dispatch(
            updateVmoBotThunkDetails({
              conversationId: chat_session_id,
            })
          );
        }
        if (chat_session_title) {
          dispatch(
            updateVmoBotThunkDetails({
              conversationName: chat_session_title,
            })
          );
        }
      }
    } catch (err: any) {
      const errorMessage = err?.response?.data?.status_message;
      getToast("Error", errorMessage || "Files could not be uploaded");
      dispatch(resetVmoBotChatThunk());
      dispatch({
        type: VMO_BOT_ACTIONS.UPDATE_PROCESSING_STAGE,
        payload: "SelectFiles",
      });
    }
  };
};

export const getContractsReviewThunk = (verticalId?: any) => {
  return async (dispatch: any) => {
    try {
      dispatch({
        type: CONTRACT_REVIEW_ACTIONS.SET_CONTRACT_REVIEW_DATA,
        payload: {
          loading: true,
          error: {},
        },
      });

      const res = await getContractsReview(verticalId);
      const data = new ContractReviewModel(res);

      dispatch({
        type: CONTRACT_REVIEW_ACTIONS.SET_CONTRACT_REVIEW_DATA,
        payload: {
          ...data,
          loading: false,
          error: {},
        },
      });
    } catch (error) {
      dispatch({
        type: CONTRACT_REVIEW_ACTIONS.SET_CONTRACT_REVIEW_DATA,
        payload: {
          loading: false,
          error: error,
          list: [],
        },
      });
    }
  };
};

export const getFailedClauseThunk = () => {
  return async (dispatch: any) => {
    try {
      const failedDocRes = await getClauseCheckDocumentsDataForDashboard({
        clause_status: [clauseCheckDocStatus.FAILED_WHILE_REVIEWING],
        page: 1,
        page_size: 100,
      });

      const sanitizedFaileDocRes = new BulkClauseTableData(failedDocRes);
      dispatch({
        type: CLAUSE_CHECK_ACTIONS.SET_FAILED_DOC_DATA,
        payload: sanitizedFaileDocRes,
      });
    } catch (err) {}
  };
};

export const getClauseCheckThunk = ({ polling }: { polling: boolean }) => {
  return async (dispatch: any) => {
    try {
      if (!polling) {
        dispatch({
          type: CLAUSE_CHECK_ACTIONS.SET_CLAUSE_CHECK_DATA,
          payload: {
            loading: true,
            error: {},
          },
        });
      }

      const res = await getClauseCheckDataForDashboard();
      const data = new ClauseCheckModel(res);

      const failedDocRes = await getClauseCheckDocumentsDataForDashboard({
        clause_status: [clauseCheckDocStatus.FAILED_WHILE_REVIEWING],
        page: 1,
        page_size: 100,
      });

      const sanitizedFaileDocRes = new BulkClauseTableData(failedDocRes);
      const dashboardData = await getClauseCheckDocumentsDataForDashboard();

      const documents = dashboardData?.results;

      const sanitizedDocsData = documents?.map(
        (doc: any) => new ContractDocument(doc)
      );

      const updatedClauseCheckData = {
        ...data,
        pending_documents: {
          list: sanitizedDocsData,
        },
        failed_documents: sanitizedFaileDocRes,
      };

      dispatch({
        type: CLAUSE_CHECK_ACTIONS.SET_CLAUSE_CHECK_DATA,
        payload: {
          ...updatedClauseCheckData,
          loading: false,
          error: {},
        },
      });
    } catch (error) {
      dispatch({
        type: CLAUSE_CHECK_ACTIONS.SET_CLAUSE_CHECK_DATA,
        payload: {
          loading: false,
          error: error,
        },
      });
    }
  };
};

export const updateBotTitle = (payload: {
  conversationId: string;
  data: { title: string };
  entity: string;
}) => {
  return async (dispatch: any) => {
    try {
      await dispatch(
        updateVmoBotThunkDetails({ conversationName: payload.data.title })
      );
      dispatch(updateDetailsOfConversation(payload));
    } catch (error) {
      console.error("Error updating bot title:", error);
    }
  };
};

export const updateADKBotThunkDetails = (payload: any) => {
  return async (dispatch: any) => {
    dispatch({
      type: VMO_BOT_ACTIONS.UPDATE_VMO_BOT_DATA,
      payload: payload,
    });
  };
};

export const updateVmoBotThunkDetails = (payload: any) => {
  return async (dispatch: any) => {
    dispatch({
      type: VMO_BOT_ACTIONS.UPDATE_VMO_BOT_DATA,
      payload: payload,
    });
  };
};

export const updateVmoBotModel = (data: any) => {
  return async (dispatch: any) => {
    dispatch({
      type: VMO_BOT_ACTIONS.SET_SELECTED_MODEL,
      payload: data,
    });
  };
};

export const sendMessageToVmoBotThunk = ({
  message,
  isStreaming = false,
}: {
  message: string;
  isStreaming?: boolean;
}) => {
  return async (dispatch: any, getState: any) => {
    const date = new Date().getTime();
    const userMessageId = date + 1;
    const botMessageId = date + 2;

    try {
      if (!isStreaming) {
        await dispatch({
          type: VMO_BOT_ACTIONS.ADD_MESSAGE_TO_CHAT,
          payload: {
            sender: "user",
            text: message,
            id: userMessageId,
          },
        });
      }

      await dispatch({
        type: VMO_BOT_ACTIONS.SET_MESSAGE_LOADER,
        payload: true,
      });

      await dispatch({
        type: VMO_BOT_ACTIONS.ADD_MESSAGE_TO_CHAT,
        payload: {
          sender: "bot",
          text: "",
          id: botMessageId,
          loading: true,
        },
      });

      const vmoBotData = getState()?.rootReducer?.vscDashboardReducer?.vmoBot;

      const { conversationId, fileUpload, selectedFiles } = vmoBotData;

      const processedFiles =
        fileUpload?.processedFiles?.[DASHBOARD_ENTITIES.TALK_TO_CONTRACT] || [];

      const { gcsFileNames } = getFileNamesFromSelectedFileIds(
        selectedFiles,
        processedFiles
      );

      const botResponse = await getVmoBotResponse({
        files: gcsFileNames,
        question: message,
        chat_session_id: conversationId,
      });

      const { answer = [], suggested_questions = [] } = botResponse;

      await dispatch({
        type: VMO_BOT_ACTIONS.UPDATE_MESSAGE_DATA,
        payload: {
          id: botMessageId,
          text: answer,
          loading: false,
        },
      });

      await dispatch({
        type: VMO_BOT_ACTIONS.SET_MESSAGE_LOADER,
        payload: false,
      });

      const suggestions = suggested_questions?.map(
        (suggestion: any) => new Suggestion(suggestion)
      );

      await dispatch({
        type: VMO_BOT_ACTIONS.SET_SUGGESTIONS,
        payload: suggestions || defaultSuggestions,
      });
    } catch (error) {
      getToast("Error", "Something went wrong.");

      await dispatch({
        type: VMO_BOT_ACTIONS.SET_MESSAGE_LOADER,
        payload: false,
      });

      await dispatch({
        type: VMO_BOT_ACTIONS.UPDATE_MESSAGE_DATA,
        payload: {
          id: botMessageId,
          text: "The requested information couldn't be retrieved. Please try again later.",
          loading: false,
          errored: true,
        },
      });
    }
  };
};

export const sendSuggestionToVmoBotThunk = (suggestion: { text: string }) => {
  return async (dispatch: any) => {
    try {
      dispatch({
        type: VMO_BOT_ACTIONS.REMOVE_SUGGESTION,
        payload: {
          text: suggestion?.text,
        },
      });

      await dispatch(sendMessageToVmoBotThunk({ message: suggestion?.text }));
    } catch (error) {}
  };
};

export const resetADKBotChatThunk = (payload?: any) => {
  return async (dispatch: any) => {
    try {
      dispatch({
        type: ADK_TTC_BOT_ACTIONS.RESET_ADK_CHAT_BOT,
        payload: payload,
      });
    } catch (error) {}
  };
};

export const resetVmoBotChatThunk = (payload?: any) => {
  return async (dispatch: any) => {
    try {
      dispatch({
        type: VMO_BOT_ACTIONS.RESET_VMO_BOT_CHAT,
        payload: payload,
      });
    } catch (error) {}
  };
};

export const setOrUpdateContractExpiryDashboardData = (data: any) => {
  return async (dispatch: any) => {
    try {
      dispatch({
        type: CONTRACT_EXPIRY_DASHBOARD_ACTIONS.SET_CONTRACT_EXPIRY_DASHBOARD_DATA,
        payload: data,
      });
    } catch (error) {
      console.error(error);
    }
  };
};

export const setOrUpdateContractExpiryDataForGraph = ({
  vertical_id,
  from_date,
  to_date,
}: {
  vertical_id: string;
  from_date: string;
  to_date: string;
}) => {
  return async (dispatch: any) => {
    try {
      const res = await getDueContractsDataForGraph({
        from_date: from_date,
        to_date: to_date,
        vertical_id: vertical_id,
      });

      dispatch({
        type: CONTRACT_EXPIRY_DASHBOARD_ACTIONS.SET_CONTRACT_EXPIRY_DASHBOARD_GRAPH_DATA,
        payload: res?.data,
      });
    } catch (error) {
      console.error(error);
    }
  };
};

export const loadChatHistory = (chatSessionId: string) => {
  return async (dispatch: any, getState: any) => {
    try {
      const vmoBot = getState()?.rootReducer?.vscDashboardReducer?.vmoBot;
      const { fileUpload } = vmoBot;

      dispatch(
        updateVmoBotThunkDetails({
          view: "chat",
          fileUpload: {
            ...fileUpload,
            processingStage: "loading",
          },
        })
      );

      const res = await getConversationData(chatSessionId);

      const { id, title, messages, model } = res;

      const { chat, processedFiles, selectedFiles } =
        getSanitizedChatMessages(messages);

      dispatch(
        updateVmoBotThunkDetails({
          conversationId: id,
          conversationName: title,
          chat: chat,
          fileUpload: {
            ...fileUpload,
            processingStage: "ShowUploadedFiles",
            uploadingState: false,
            processedFiles: {
              ...fileUpload?.processedFiles,
              [DASHBOARD_ENTITIES.TALK_TO_CONTRACT]: processedFiles,
            },
            uploadedFiles: processedFiles,
          },
          selectedFiles: selectedFiles,
          selectedModel: model,
        })
      );
    } catch (error: any) {
      const { message, status_message } = error?.response?.data || {};
      const errorMessage =
        message || status_message || "Error loading chat history";
      getToast("Error", errorMessage);
      dispatch(resetVmoBotChatThunk());
    }
  };
};

export const loadADKChatHistory = (
  chatSessionId: string,
  setSearchParams: (fn: (prev: URLSearchParams) => URLSearchParams) => void
) => {
  return async (dispatch: any, getState: any) => {
    try {
      const adkTTCBot = getState()?.rootReducer?.vscDashboardReducer?.adkTTCBot;
      const { data } = getState()?.rootReducer.userInfoReducer.profileData;
      const userId = data?.id || "me";
      const { apps } = adkTTCBot;

      dispatch(
        setAdkTTCBotActionsThunk({
          view: "chat",
          processingStage: "loading",
        })
      );
      const res = await getADKConversationData(
        chatSessionId,
        userId,
        apps?.[0] || "rag"
      );
      const { id, events, state: sessionState } = res;
      const storedStatus = sessionState?.contract_status || "active";
      const chat: Array<any> = [];
      let thoughtText = [];
      let hideNextBotMessage = false;
      for (const event of events) {
        const { content } = event || {};
        const { role, parts = [] } = content || {};

        for (const part of parts) {

          if (part?.thought) {
            thoughtText.push(part.text);
            continue;
          }

          if (part?.text && (role === "user" || role === "model")) {
            // Check if this is a corpus ID message
            const isCorpusMessage = role === "user" && part.text.includes("Use Corpus ID:");
            
            if (thoughtText.length > 0) {
              chat.push({
                sender: "bot",
                text: thoughtText,
                id: event.id,
                thought: true,
                hidden: hideNextBotMessage,
              });
              thoughtText = [];
            }

            chat.push({
              sender: role === "user" ? "user" : "bot",
              text: part.text,
              id: event.id,
              thought: false,
              hidden: isCorpusMessage || hideNextBotMessage,
            });
            
            // If this is a corpus message, hide the next bot response
            if (isCorpusMessage) {
              hideNextBotMessage = true;
            } else if (hideNextBotMessage && role === "model") {
              // Reset after hiding bot response
              hideNextBotMessage = false;
            }
          }
        }
      }

      // If leftover thoughtText exists at end, push it (optional, if needed)
      if (thoughtText.length > 0) {
        chat.push({
          sender: "bot",
          text: thoughtText,
          id: events[events.length - 1]?.id,
          thought: true,
        });
        thoughtText = [];
      }

      const title = res?.appName + " " + id;
      dispatch(
        setAdkTTCBotActionsThunk({
          conversationName: title,
          chat: [...chat],
          processingStage: "idle",
          sessionId: id,
          contractStatus: storedStatus,
        })
      );

      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (next.get("session") === id) return prev;
        next.set("session", id);
        return next;
      });
    } catch (error: any) {
      const { message, status_message } = error?.response?.data || {};
      const errorMessage =
        message || status_message || "Error loading chat history";
      getToast("Error", errorMessage);
      dispatch(resetADKBotChatThunk());
    }
  };
};

// ============== V2 Thunks for Agentic Dashboard ==============

export const setOrUpdateContractExpiryDataForGraphV2 = ({
  vertical_id,
  from_date,
  to_date,
}: {
  vertical_id?: string;
  from_date: string;
  to_date: string;
}) => {
  return async (dispatch: any) => {
    try {
      const res = await getAboutToExpireContractsDataForGraphV2({
        from_date: from_date,
        to_date: to_date,
        vertical_id: vertical_id,
      });

      dispatch({
        type: CONTRACT_EXPIRY_DASHBOARD_ACTIONS.SET_CONTRACT_EXPIRY_DASHBOARD_GRAPH_DATA,
        payload: res?.data,
      });
    } catch (error) {
      console.error(error);
    }
  };
};
